import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { verifyOtp, resendOtp, forceAuthRefresh } = useAuth();
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [resendDisabled, setResendDisabled] = useState(true);

  useEffect(() => {
    const state = location.state as {
      email?: string;
      userId?: string;
      from?: string;
    };

    if (!state?.userId) {
      return navigate("/login");
    }

    setEmail(state.email || "");
    setUserId(state.userId);

    setCountdown(60);
    setResendDisabled(true);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [location, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5)
      document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6)
      return setError("Please enter a valid 6-digit OTP");

    setLoading(true);
    setError("");

    try {
      const result = await verifyOtp(userId, otpValue);
      setSuccess(true);
      toast.success("Login successful!");

      // Force refresh the auth state and then navigate
      setTimeout(async () => {
        try {
          await forceAuthRefresh();

          const state = location.state as { from?: string };
          const dashboard =
            result.user.role === "admin"
              ? "/dashboard/admin"
              : result.user.role === "developer"
              ? "/dashboard/developer"
              : "/dashboard/client";

          // Use replace to prevent back navigation to OTP page
          navigate(state?.from || dashboard, { replace: true });
        } catch (error) {
          // Fallback navigation
          const state = location.state as { from?: string };
          const dashboard =
            result.user.role === "admin"
              ? "/dashboard/admin"
              : result.user.role === "developer"
              ? "/dashboard/developer"
              : "/dashboard/client";
          navigate(state?.from || dashboard, { replace: true });
        }
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to verify OTP");
      toast.error(err.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendDisabled(true);
    setError("");
    try {
      await resendOtp(userId);
      setCountdown(60);
      toast.info("OTP has been resent to your email");
    } catch (err: any) {
      setError(err.message || "Failed to resend OTP");
      toast.error(err.message || "Failed to resend OTP");
      setResendDisabled(false);
    }
  };

  if (success) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          theme === "dark" ? "bg-gray-900" : "bg-white"
        }`}
      >
        <div className="text-center space-y-6">
          <Building2
            className={`mx-auto w-10 h-10 ${
              theme === "dark" ? "text-blue-400" : "text-blue-600"
            }`}
          />
          <CheckCircle
            className={`mx-auto w-16 h-16 ${
              theme === "dark" ? "text-green-400" : "text-green-600"
            }`}
          />
          <h2
            className={`text-2xl font-bold ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            Verification Successful
          </h2>
          <p
            className={`${
              theme === "dark" ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Redirecting to your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${
        theme === "dark" ? "bg-gray-900" : "bg-white"
      } py-12 px-4`}
    >
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Building2
            className={`mx-auto w-10 h-10 ${
              theme === "dark" ? "text-blue-400" : "text-blue-600"
            }`}
          />
          <h2
            className={`mt-6 text-3xl font-extrabold ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            Verify Your Identity
          </h2>
          <p
            className={`${
              theme === "dark" ? "text-gray-300" : "text-gray-600"
            }`}
          >
            We sent a 6-digit code to <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 border rounded text-sm text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300">
              <AlertCircle className="inline w-4 h-4 mr-1" /> {error}
            </div>
          )}

          <div className="flex justify-center space-x-2">
            {otp.map((digit, idx) => (
              <input
                key={idx}
                id={`otp-${idx}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                className="w-12 h-12 text-center border rounded text-xl font-bold"
                required
              />
            ))}
          </div>

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm flex items-center text-blue-600 dark:text-blue-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
            </button>
            <button
              type="button"
              disabled={resendDisabled}
              onClick={handleResendOtp}
              className={`text-sm ${
                resendDisabled
                  ? "text-gray-400"
                  : "text-blue-600 hover:underline"
              }`}
            >
              {resendDisabled ? `Resend in ${countdown}s` : "Resend code"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading || otp.some((d) => !d)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded disabled:opacity-50"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OtpVerification;
